import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MobileLogo from '../Layout/MobileLogo';
import '../../styles/VariantPrint.css';

/* Печатный бланк варианта: лист A4 (794×1123 px при 96 dpi).
   Страницы режем сами, а не полагаемся на браузер: так номер не рвётся в
   случайном месте, на каждом листе своя нумерация и влезает максимум номеров.
   Компонент только рисует листы; что с ними делать — печатать или собирать в
   PDF — решает тот, кто его подключил (см. onReady). */
const PAGE_H = 1123;
const PAD_TOP = 64, PAD_BOTTOM = 54;
const GAP = 24;                     // зазор между номерами
const GAP_IN = 12;                  // зазор внутри номера (условие → файл → ответ)
const LOGO = `${process.env.PUBLIC_URL || ''}/landing/logo-trim.png`;

export const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

const fileNames = (hw) => {
  const files = Array.isArray(hw.attachedFiles) ? hw.attachedFiles : [];
  return files.map(f => f?.name || f?.originalName || '').filter(Boolean);
};

/* Номер разбираем на блоки: абзацы условия, строка с файлом, поле ответа —
   чтобы длинное задание могло перетечь на следующий лист. */
const atomsOf = (hw, n) => {
  const box = document.createElement('div');
  box.innerHTML = hw.description || '';
  const blocks = [...box.children].map(el => el.outerHTML);
  if (!blocks.length && String(hw.description || '').trim()) blocks.push(`<p>${hw.description}</p>`);
  const atoms = blocks.map(html => ({ kind: 'html', html, gap: 0 }));
  const names = fileNames(hw);
  if (names.length) atoms.push({ kind: 'file', names, gap: GAP_IN });
  atoms.push({ kind: 'answer', gap: GAP_IN });
  // Первый блок номера отбиваем от предыдущего, остальные идут вплотную
  return atoms.map((a, i) => ({ ...a, n, first: i === 0, gap: i === 0 ? GAP : a.gap, key: `${hw.id}-${i}` }));
};

/* Строка бланка. Цифру показываем один раз — у начала номера. */
const Atom = ({ atom, top }) => (
  <div className={`pdfb-row${top ? ' top' : ''}${atom.first ? ' first' : atom.gap ? ' gap' : ''}`}>
    <div className="pdfb-num">{atom.first ? atom.n : ''}</div>
    <div className="pdfb-main">
      {atom.kind === 'html' && <div className="pdfb-text" dangerouslySetInnerHTML={{ __html: atom.html }} />}
      {atom.kind === 'file' && <div className="pdfb-file">Файл к заданию: <b>{atom.names.join(', ')}</b></div>}
      {atom.kind === 'answer' && (
        <div className="pdfb-ans"><span className="pdfb-ans-l">Ответ</span><span className="pdfb-ans-line" /></div>
      )}
    </div>
  </div>
);

export default function VariantBlank({ lesson, onReady, pagesRef }) {
  const [pages, setPages] = useState(null);
  const rulerRef = useRef(null);
  const headRef = useRef(null);
  const slimRef = useRef(null);
  const footRef = useRef(null);
  const ownRef = useRef(null);
  const boxRef = pagesRef || ownRef;

  // Oswald нужен только бланку — подключаем на время его жизни
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Oswald:wght@500;600&display=block';
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  const hw = lesson?.Homework || [];
  const atoms = useMemo(() => hw.flatMap((h, i) => atomsOf(h, i + 1)), [hw]);

  // Раскладка: меряем каждый блок в линейке и набиваем листы доверху
  const layout = useCallback(() => {
    const ruler = rulerRef.current;
    if (!ruler || !atoms.length) return;
    const blocks = [...ruler.querySelectorAll('[data-atom]')];
    if (blocks.length !== atoms.length) return;
    const H = blocks.map(b => b.getBoundingClientRect().height);

    const headH = headRef.current?.offsetHeight || 0;
    const slimH = slimRef.current?.offsetHeight || 0;
    const footH = footRef.current?.offsetHeight || 0;
    const usable = (first) => PAGE_H - PAD_TOP - PAD_BOTTOM - (first ? headH : slimH) - footH;
    const heightOf = (list) => list.reduce((s, k, j) => s + H[k] + (j ? (atoms[k].gap || 0) : 0), 0);

    const out = [];
    let page = [];
    for (let i = 0; i < atoms.length; i++) {
      const margin = page.length ? (atoms[i].gap || 0) : 0;
      const left = usable(out.length === 0) - heightOf(page);
      if (page.length && H[i] + margin > left) {
        // Ищем осмысленное место разрыва, а не «где кончилось место»:
        let cut = i;
        //  • строку с файлом и поле ответа не отрываем от условия
        while (cut > 0 && atoms[cut].kind !== 'html' && page.includes(cut - 1)) cut--;
        //  • не оставляем на листе один-единственный блок номера
        const sameLeft = page.filter(k => atoms[k].n === atoms[cut].n && k < cut).length;
        if (sameLeft === 1) cut--;
        //  • и не переносим один блок, если это не ответ — лучше утащить пару
        const pos = page.indexOf(cut);
        const moved = pos >= 0 ? page.splice(pos) : [];
        if (page.length) out.push(page); else if (moved.length) { out.push(moved.splice(0, 1)); }
        page = moved;
      }
      page.push(i);
    }
    if (page.length) out.push(page);
    setPages(out);
  }, [atoms]);

  // Считаем, когда шрифты и картинки готовы; одна залипшая картинка не должна
  // оставлять бланк без разбивки, поэтому ждём их не дольше четырёх секунд.
  useEffect(() => {
    if (!atoms.length) return;
    let cancelled = false;
    let late = null;
    const waitImages = () => {
      const imgs = [...(rulerRef.current?.querySelectorAll('img') || [])].filter(i => !i.complete);
      if (!imgs.length) return Promise.resolve();
      const done = Promise.all(imgs.map(img => new Promise(r => {
        img.addEventListener('load', r, { once: true });
        img.addEventListener('error', r, { once: true });
      })));
      return Promise.race([done, new Promise(r => setTimeout(r, 4000))]);
    };
    const run = async () => {
      /* document.fonts.ready иногда не выполняется вовсе — например, когда в
         вёрстке есть шрифт, который браузер так и не начал грузить. Бланк тогда
         не собирался совсем, и скачивание падало с «бланк не собрался».
         Ждём шрифты не дольше трёх секунд: без них разметка почти та же. */
      try {
        await Promise.race([document.fonts.ready, new Promise(r => setTimeout(r, 3000))]);
      } catch { /* нет поддержки — не беда */ }
      await waitImages();
      if (cancelled) return;
      layout();
      // пересчитываем позже только если картинка так и не догрузилась
      const pending = [...(rulerRef.current?.querySelectorAll('img') || [])].some(i => !i.complete);
      if (pending) late = setTimeout(() => { if (!cancelled) layout(); }, 2500);
    };
    run();
    return () => { cancelled = true; clearTimeout(late); };
  }, [atoms.length, layout]);

  /* Листы готовы — отдаём их наружу ровно один раз.

     Картинки на самих листах — это уже другие узлы, не те, что ждали в
     «линейке»: разметка пересобирается при разбивке на страницы. Поэтому
     перед выдачей ждём их загрузки ещё раз. Ждём через опрос, а не через
     обработчики: эффект пересоздаётся на каждом рендере, и подписка вместе с
     флагом отмены успевала сброситься раньше, чем картинка догружалась —
     onReady не звали вовсе, и сборка висела на «Готовим PDF…». */
  const handedOff = useRef(false);
  useEffect(() => {
    if (!pages || !onReady || handedOff.current) return;
    let stop = false;
    const started = Date.now();
    const tick = () => {
      if (stop || handedOff.current) return;
      const els = [...(boxRef.current?.querySelectorAll('.pdfb-page') || [])];
      const pending = els.flatMap(el => [...el.querySelectorAll('img')])
        .some(i => !i.complete || !i.naturalWidth);
      // одна залипшая картинка не должна блокировать скачивание целиком
      if (!pending || Date.now() - started > 8000) {
        handedOff.current = true;
        onReady(els);
        return;
      }
      setTimeout(tick, 150);
    };
    const t = setTimeout(tick, 60);
    return () => { stop = true; clearTimeout(t); };
  }, [pages, onReady, boxRef]);

  if (!lesson) return null;

  const total = hw.length;
  const maxScore = hw.reduce((s, h) => s + (h.maxScore || 1), 0);
  const date = fmtDate(lesson.date || lesson.created_at);

  const Head = ({ slim }) => (
    <>
      <div className={`pdfb-head${slim ? ' slim' : ''}`}>
        <div className="pdfb-head-l">
          {!slim && <img className="pdfb-logo" src={LOGO} alt="ИНФА СОТКА" />}
          <div className="pdfb-title">{lesson.title}</div>
        </div>
        {date && <div className="pdfb-date">{date}</div>}
      </div>
      {slim && <div className="pdfb-head-gap" />}
      {!slim && (
        <div className="pdfb-meta">
          <span>{total} {plural(total, ['задание', 'задания', 'заданий'])}</span>
          <span>макс. {maxScore} {plural(maxScore, ['первичный', 'первичных', 'первичных'])}</span>
        </div>
      )}
    </>
  );

  const list = pages || [atoms.map((_, i) => i)];
  return (
    <>
      <div className="pdfb-pages" ref={boxRef}>
        {list.map((idxs, pi) => (
          <div className="pdfb-page" key={pi}>
            <Head slim={pi > 0} />
            <div className="pdfb-tasks">
              {idxs.map((i, k) => (
                <Atom key={atoms[i].key} atom={atoms[i]} top={k === 0} />
              ))}
            </div>
            <div className="pdfb-foot">
              <MobileLogo />
              <span className="pdfb-pnum">{pi + 1} / {list.length}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Линейка для замеров — тот же лист и та же ширина колонки, но за экраном */}
      <div className="pdfb-ruler" ref={rulerRef} aria-hidden="true">
        <div className="pdfb-ruler-page">
          <div ref={headRef}><Head /></div>
          <div ref={slimRef}><Head slim /></div>
          <div ref={footRef}><div className="pdfb-foot"><MobileLogo /><span className="pdfb-pnum">1 / 1</span></div></div>
          {atoms.map(a => (
            <div data-atom key={a.key}><Atom atom={a} top /></div>
          ))}
        </div>
      </div>
    </>
  );
}
